<script setup>
import { computed, defineAsyncComponent, nextTick, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageHead } from '@/composables/usePageHead'
import content from '@/content/our-work.yml'
import ContentHero from '@/components/content/ContentHero.vue'
import OurWorkCard from '@/components/OurWorkCard.vue'
import LinkCardsBlock from '@/components/content/LinkCardsBlock.vue'
import ResourceSearch from '@/components/ResourceSearch.vue'
import { sectionCardsBlock } from '@/utils/sectionCards'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { isReviewLive } from '@/cms/reviewGate.js'

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsEditButton = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditButton.vue'))
  : null
const CmsNewContentButton = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsNewContentButton.vue'))
  : null

const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

usePageHead(content)

// Splash cards linking to the other "What we do" pages — same treatment as
// /what-we-do, derived from the section's single source of truth.
const linkCards = computed(() => sectionCardsBlock('what-we-do', content.head.slug))

const route  = useRoute()
const router = useRouter()

const modules = import.meta.glob('/src/content/our-work/*.md', { eager: true })

const now = new Date()
function isPublished(publishedAt) {
  if (import.meta.env.DEV) return true
  if (!publishedAt) return true
  return new Date(publishedAt) <= now
}

const items = Object.entries(modules)
  .filter(([path]) => path.endsWith('.md') && !path.endsWith('/README.md'))
  .map(([path, mod]) => ({
    slug:        path.replace('/src/content/our-work/', '').replace('.md', ''),
    contentPath: path,
    title:       mod.title,
    date:        mod.date,
    excerpt:     mod.excerpt,
    image:       mod.image || '',
    publishedAt: mod.publishedAt || null,
    reviewStatus: mod.reviewStatus || '',
  }))
  .filter(i => isPublished(i.publishedAt) && isReviewLive(i.reviewStatus))
  .sort((a, b) => new Date(b.date) - new Date(a.date))

const query = ref(route.query.q || '')

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (q === '') return items
  return items.filter(i =>
    (i.title || '').toLowerCase().includes(q)
    || (i.excerpt || '').toLowerCase().includes(q)
  )
})

function clearSearch() {
  query.value = ''
  router.replace({ query: {} })
}

const resultsSection = ref(null)

function onSearch(value) {
  query.value = value
  const nextQuery = { ...route.query }
  if (value) nextQuery.q = value
  else delete nextQuery.q
  router.replace({ query: nextQuery })
  // Scroll position is preserved (main.js's scrollBehavior), so nudge the
  // results into view ourselves — smoothly, from wherever the page already
  // is, rather than the hard jump-to-top this used to cause.
  nextTick(() => resultsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}
</script>

<template>
  <div class="our-work">
    <ContentHero
      :hero="{ headline: content.intro.heading, intro: content.intro.lead }"
      colour="lime"
    />

    <div class="container">
    <p class="body">{{ content.intro.body }}</p>

    <ResourceSearch
      :modelValue="query"
      label="Search projects"
      input-id="our-work-search"
      placeholder="Search by project name…"
      @search="onSearch"
    />

    <div class="header-row" ref="resultsSection">
      <h2 class="grid-heading">{{ content.intro.gridHeading }}</h2>
      <CmsNewContentButton v-if="showCmsChrome" type="our-work" />
    </div>

    <p v-if="filtered.length === 0" class="no-results">
      No projects match your search.
      <button class="link-btn" @click="clearSearch">Clear search</button>
    </p>

    <div v-else class="grid">
      <template v-for="item in filtered" :key="item.slug">
        <CmsEditButton v-if="showCmsChrome" :content-path="item.contentPath" :review-status="item.reviewStatus">
          <OurWorkCard
            :slug="item.slug"
            :title="item.title"
            :excerpt="item.excerpt"
            :image="item.image"
          />
        </CmsEditButton>
        <OurWorkCard
          v-else
          :slug="item.slug"
          :title="item.title"
          :excerpt="item.excerpt"
          :image="item.image"
        />
      </template>
    </div>

    <section class="links">
      <LinkCardsBlock :block="linkCards" />
    </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.our-work {
  padding-bottom: $space-8;

  // The lead is one short sentence; drop ContentHero's default 48ch measure
  // so it sits on a single line at desktop width (still wraps naturally on
  // narrower viewports rather than overflowing).
  :deep(.content-hero .intro) { max-width: none; }

  .body { color: var(--color-text); margin: $space-6 0; }

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    margin-bottom: $space-5;
  }

  .grid-heading {
    font-size: $font-size-h3;
    color: var(--color-ink);
    margin-bottom: 0;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: 1fr;   // equalise every row to the tallest → uniform card heights
    gap: $space-5;

    @include from-md { grid-template-columns: repeat(2, 1fr); }
    @include from-lg { grid-template-columns: repeat(3, 1fr); }
  }

  // The CMS edit wrapper is the grid item in CMS mode; make it fill the cell
  // so the card inside can stretch to a consistent height too.
  :deep(.cms-edit-wrap) { height: 100%; }

  .no-results {
    color: var(--color-text-muted);
    padding-block: $space-8;
  }

  .link-btn {
    background: none;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
    font-family: $font-sans;
    font-size: inherit;
    padding: 0;
    margin-left: $space-2;
    &:hover         { text-decoration: underline; }
    &:focus-visible { @include focus-ring; }
    &:active        { color: var(--color-primary-press); }
  }

  .links {
    padding-top: $space-8;
    margin-top: $space-10;
    border-top: 1px solid var(--color-border);
  }
}
</style>
